import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Space,
    message,
    Popconfirm,
    Avatar,
    Typography,
} from "antd";
import { PlusOutlined, SearchOutlined, FileImageOutlined, ReloadOutlined } from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import ImageUpload from "../components/ImageUpload";

const { Paragraph } = Typography;

const NewsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listNews();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((n) =>
            [n.title, n.subtitle, n.content]
                .filter(Boolean)
                .some((t) => t.toLowerCase().includes(q))
        );
    }, [items, search]);

    const openEdit = (n) => {
        setEditing(n);
        form.setFieldsValue(n);
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
    };

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            if (editing._id) {
                await api.updateNews(editing._id, v);
                message.success("News updated");
            } else {
                await api.createNews(v);
                message.success("News published");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteNews(id);
            message.success("Deleted");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Title",
            key: "title",
            render: (_, r) => (
                <Space>
                    <Avatar shape="square" src={r.image} icon={<FileImageOutlined />} />
                    <div>
                        <div style={{ fontWeight: 600 }}>{r.title}</div>
                        {r.subtitle && <div className="admin-cell-sub">{r.subtitle}</div>}
                    </div>
                </Space>
            ),
        },
        {
            title: "Published",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            defaultSortOrder: "descend",
            sorter: (a, b) =>
                new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(r)}>
                        Edit
                    </Button>
                    <Popconfirm title="Delete?" onConfirm={() => remove(r._id)}>
                        <Button size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">News</h1>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        New entry
                    </Button>
                </Space>
            </div>

            <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search news"
                style={{ width: 280, marginBottom: 16 }}
                onChange={(e) => setSearch(e.target.value)}
            />

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={filtered}
                scroll={{ x: "max-content" }}
                onRow={(record) => ({
                    className: "admin-row-clickable",
                    onClick: (e) => {
                        if (e.target.closest("button")) return;
                        setPreview(record);
                    },
                })}
            />

            {/* Preview */}
            <Modal
                open={!!preview}
                title={preview ? preview.title : ""}
                onCancel={() => setPreview(null)}
                footer={null}
                width={640}
            >
                {preview && (
                    <>
                        {preview.image && (
                            <img
                                src={preview.image}
                                alt={preview.title}
                                style={{ width: "100%", borderRadius: 8, marginBottom: 12 }}
                            />
                        )}
                        {preview.subtitle && (
                            <h3 style={{ marginTop: 0 }}>{preview.subtitle}</h3>
                        )}
                        <span className="admin-cell-sub">
                            Published {fmtDate(preview.createdAt, true)}
                        </span>
                        <Paragraph style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>
                            {preview.content || "No content."}
                        </Paragraph>
                    </>
                )}
            </Modal>

            {/* Create / edit */}
            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit news" : "Publish news"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                width={600}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="subtitle" label="Subtitle">
                        <Input />
                    </Form.Item>
                    <Form.Item name="content" label="Content">
                        <Input.TextArea rows={6} />
                    </Form.Item>
                    <Form.Item name="image" label="Image">
                        <ImageUpload />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default NewsPage;
